import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { ConnectionInsight, ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { ConnectionFollowers } from '../ConnectionFollowers';
import { EarlyAccessServiceMark } from './EarlyAccessServiceMark';
import { earlyAccessMessages } from './earlyAccessMessages';
import { catalogServiceName, isDistributor } from './earlyAccessCopy';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import catalogStyles from '../Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole early-access flow; each part uses its own slice of it */
import styles from './EarlyAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectionCardEarlyAccessProps {
  connection: ServiceConnection;
  /** The daily read of this service's public profile, when the artist saved one. */
  insight?: ConnectionInsight;
  onEditProfileUrl: (integrationType: string) => void;
  /** True while any write on the strip is in flight. */
  isBusy: boolean;
}

/**
 * The card of a service that has no sign-in in the Music24 app yet (Amazon Music for Artists). The
 * web offers no waiting list (owner decision 2026-10-19): every other service signs in in the app.
 * The card says "Coming soon" and keeps only the profile url.
 *
 * design: connector-waitlist a (Connection Deck), a2 card states
 */
const ConnectionCardEarlyAccessComponent: React.FC<ConnectionCardEarlyAccessProps> = ({
  connection,
  insight,
  onEditProfileUrl,
  isBusy,
}) => {
  const intl = useIntl();
  const { integrationType, artistProfileUrl } = connection;

  const name = catalogServiceName(intl, integrationType);
  // A distributor has no public artist profile, so it has no profile url.
  const takesProfileUrl = !isDistributor(integrationType);
  const hasProfileUrl = artistProfileUrl !== null && artistProfileUrl !== '';
  const editProfileUrl = useCallback(() => onEditProfileUrl(integrationType), [integrationType, onEditProfileUrl]);

  return (
    <div
      className={clsx(catalogStyles.connectionCard, catalogStyles.connectionCardOff)}
      data-testid={`early-access-card-${integrationType}`}
    >
      <EarlyAccessServiceMark integrationType={integrationType} />
      <div className={catalogStyles.connectionBody}>
        <div className={catalogStyles.connectionName}>
          <span className={styles.dot} />
          {name}
        </div>
        <div className={clsx(catalogStyles.connectionHandle, styles.handle)}>
          {intl.formatMessage(earlyAccessMessages.comingSoon)}
        </div>
        {insight !== undefined && <ConnectionFollowers insight={insight} />}
        <div className={catalogStyles.connectionNote}>
          {withBrandWord(intl.formatMessage(earlyAccessMessages.comingSoonNote, { service: name }))}
        </div>
        {takesProfileUrl && (
          <div className={catalogStyles.connectionActions}>
            <SMLButton onClick={editProfileUrl} className={catalogStyles.smallButton} disabled={isBusy}>
              {hasProfileUrl
                ? intl.formatMessage({ id: 'app.sml.catalog.connection.edit_url', defaultMessage: 'Edit URL' })
                : intl.formatMessage({
                    id: 'app.sml.catalog.connection.add_url',
                    defaultMessage: 'Add profile URL',
                  })}
            </SMLButton>
          </div>
        )}
      </div>
    </div>
  );
};

export const ConnectionCardEarlyAccess = TypedMemo(ConnectionCardEarlyAccessComponent);
